/**
 * What the phone-country lists are, as the signed-in session holds them (RESERVATIONS_V2_PLAN.md decision 63): the mobile
 * list, the landline list and whether the business lets cashiers enter landlines. Reservations' Create form checks the
 * guest's phone number against them, and New Order v2's customer step can read the same answer.
 *
 * Two register rows follow from this file, which the reviewer adds:
 * - V1_BRIDGES.md: it READS v1's login and business stores (`loginStore.phoneCountries`, `loginStore.masterDataPicklist`,
 *   `loginStore.landlineNumberDetails`, `businessStore.businessData.allowLandlineForCountry`), which sign-in fills.
 *   When sign-in has a v2 store, only this file changes.
 * - V1_DUPLICATES.md: it COPIES the fallback order of v1's lists, which `hooks/usePhoneCountrySelection` (the mobile list)
 *   and `PhoneCountryDropdown` (the landline list) each spell out. v2 cannot import that hook (check P), so the order is
 *   written once here, and a change to v1's order must be ported.
 */
import { createSelector } from "@reduxjs/toolkit";

import { getLandlineCountriesFromPicklist, getPhoneCountriesFromPicklist, selectallowLandlineForCountry } from "./selection";
import type { PhoneCountry } from "./types";

/** The parts of v1's stores these readers look at; every one is optional, since sign-in fills them only after it ran. */
export interface PhoneCountryRoot {
  /** v1's login store: the lists sign-in fetched. */
  loginStore?: {
    /** Mobile's own key for the phone-countries list. */
    phoneCountries?: unknown;
    /** Web's key: the raw answer of the phone-countries picklist, which holds the list. */
    masterDataPicklist?: unknown;
    /** Web's derived landline list. */
    landlineNumberDetails?: unknown;
  };
  /** v1's business store: whether landline numbers are allowed. */
  businessStore?: { businessData?: { allowLandlineForCountry?: boolean } };
}

/** The phone-country lists a form checks a number against. */
export interface PhoneCountryLists {
  /** The mobile numbering rules, one per country. */
  mobile: PhoneCountry[];
  /** The landline numbering rules, one per country that publishes them, shaped like the mobile ones so the same validators take either. */
  landline: PhoneCountry[];
  /** Whether the business lets cashiers enter landline numbers. */
  landlineAllowed: boolean;
}

/** The mobile list v1 stored under mobile's key. */
function storedMobileCountriesOf(root: PhoneCountryRoot): unknown {
  return root.loginStore?.phoneCountries;
}

/** The raw picklist v1 stored under web's key. */
function masterDataPicklistOf(root: PhoneCountryRoot): unknown {
  return root.loginStore?.masterDataPicklist;
}

/** The landline list v1 stored under web's key. */
function storedLandlineCountriesOf(root: PhoneCountryRoot): unknown {
  return root.loginStore?.landlineNumberDetails;
}

/** Narrows a stored value to a list that has something in it; anything else (a missing key, an empty list) is not a list. */
function filledListOf(value: unknown): PhoneCountry[] | undefined {
  return Array.isArray(value) && value.length > 0 ? value : undefined;
}

/**
 * The phone-country lists of the session. The mobile list is the one stored under mobile's key, else the one in web's
 * picklist. The landline list is the one web stored, else the landline rules derived from whichever raw list is held
 * (mobile's key first, then the picklist). It answers the same object while the stored values are the same, so a view
 * reading it does not redraw on an unrelated store change.
 */
export const selectPhoneCountryLists = createSelector(
  [storedMobileCountriesOf, masterDataPicklistOf, storedLandlineCountriesOf, selectallowLandlineForCountry],
  (storedMobile, picklist, storedLandline, landlineAllowed): PhoneCountryLists => {
    const storedMobileList = filledListOf(storedMobile);
    const mobile = storedMobileList ?? getPhoneCountriesFromPicklist(picklist);
    const landline = filledListOf(storedLandline) ?? getLandlineCountriesFromPicklist(storedMobileList ?? picklist);
    return { mobile, landline, landlineAllowed };
  },
);
